import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../api/api";
import { tr } from "../../../i18n";
import { useContextUserId } from "../../../hooks/useSupportContext";
import { SUPPORTED_DATA_TYPES } from "../../utils/integrationWidget";

const inputStyle = { width: "100%", height: 32, borderRadius: 6, border: "1px solid #d0d5dd", padding: "0 8px", boxSizing: "border-box" };

/** Västtrafik stop picker, same search as the departures widget. */
function StopPicker({ value, onPick }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/transit/stops?q=${encodeURIComponent(query.trim())}`);
        setResults(data.stops || []);
      } catch (e) {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [query]);

  return (
    <>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{value || tr("No stop selected")}</div>
      <input type="text" value={query} placeholder={tr("Search stop, e.g. Brunnsparken")} onChange={(e) => setQuery(e.target.value)} style={inputStyle} />
      {results.length > 0 && (
        <div style={{ border: "1px solid #eaecf0", borderRadius: 6, marginTop: 6, maxHeight: 200, overflowY: "auto" }}>
          {results.map((r) => (
            <button
              key={r.gid}
              type="button"
              onClick={() => {
                onPick({ gid: r.gid, stopName: r.name.split(",")[0] });
                setQuery("");
                setResults([]);
              }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", border: 0, background: "transparent", cursor: "pointer", fontSize: 13 }}
            >
              {r.name}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Settings for an integration widget: which connected integration, its data (feed URL, place,
 * stop), rows and colours. Saving creates or updates the data source on the server (a feed) and
 * stores only its id in the layout.
 */
export default function IntegrationPanel({ obj, onPatch }) {
  const o = obj || {};
  const contextUserId = useContextUserId();
  const [connected, setConnected] = useState(null);
  const [draft, setDraft] = useState(o.config || {});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get(`/integrations/catalog/${contextUserId}`)
      .then(({ data }) => {
        if (!active) return;
        setConnected(
          (data.integrations || []).filter((i) => i.status === "connected" && SUPPORTED_DATA_TYPES.includes(i.dataType))
        );
      })
      .catch(() => active && setConnected([]));
    return () => {
      active = false;
    };
  }, [contextUserId]);

  // A different widget (or a new data source) was selected: start from its saved settings.
  useEffect(() => {
    setDraft(o.config || {});
    setError("");
  }, [o.feedId, o.integrationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (config) => {
    if (!o.integrationId) return;
    setSaving(true);
    setError("");
    try {
      const { data } = o.feedId
        ? await api.put(`/integrations/feeds/${contextUserId}/${o.feedId}`, { config })
        : await api.post(`/integrations/feeds/${contextUserId}`, { integrationId: o.integrationId, config });
      onPatch({ feedId: data.feedId, config: data.config });
    } catch (e) {
      setError(e?.response?.data?.message || tr("Could not save the data source."));
    } finally {
      setSaving(false);
    }
  };

  const color = (label, key, fallback) => (
    <div className="de-rs-row" style={{ alignItems: "center", marginBottom: 8 }}>
      <label className="de-rs-inline-label" style={{ minWidth: 100, fontSize: 12 }}>{label}</label>
      <input
        type="color"
        value={o[key] || fallback}
        onChange={(e) => onPatch({ [key]: e.target.value })}
        style={{ width: 36, height: 28, border: "none", padding: 0, cursor: "pointer", borderRadius: 4 }}
      />
    </div>
  );

  if (connected === null) {
    return <div className="de-rs-section"><span className="de-rs-section-label">{tr("Integration")}</span><p style={{ fontSize: 12 }}>{tr("Loading…")}</p></div>;
  }

  return (
    <div className="de-rs-section">
      <span className="de-rs-section-label">{tr("Integration")}</span>
      {connected.length === 0 ? (
        <p style={{ fontSize: 12, color: "#667085" }}>
          {tr("Connect an integration under Integrations (beta) first.")}{" "}
          <a href="/integrations" target="_blank" rel="noreferrer">{tr("Open Integrations (beta)")}</a>
        </p>
      ) : (
        <>
          <div className="de-rs-field" style={{ marginBottom: 8 }}>
            <label>{tr("Data from")}</label>
            <select
              value={o.integrationId || ""}
              onChange={(e) => {
                const pick = connected.find((i) => i.id === e.target.value);
                if (pick) onPatch({ integrationId: pick.id, dataType: pick.dataType, feedId: "", config: {} });
              }}
              style={{ width: "100%", height: 32, borderRadius: 6 }}
            >
              <option value="" disabled>{tr("Choose integration")}</option>
              {connected.map((i) => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>
          </div>

          {o.integrationId && o.dataType === "feed.items" && (
            <div className="de-rs-field" style={{ marginBottom: 8 }}>
              <label>{tr("Feed address (https)")}</label>
              <input type="url" value={draft.url || ""} placeholder="https://www.svt.se/nyheter/rss.xml" onChange={(e) => setDraft({ ...draft, url: e.target.value })} style={inputStyle} />
              <button type="button" className="de-text-preset" disabled={saving} onClick={() => save({ url: draft.url, limit: 10 })} style={{ width: "100%", marginTop: 6 }}>
                {saving ? tr("Saving…") : tr("Use this feed")}
              </button>
            </div>
          )}

          {o.integrationId && o.dataType === "weather.current" && (
            <div className="de-rs-field" style={{ marginBottom: 8 }}>
              <label>{tr("Place")}</label>
              <input type="text" value={draft.city || ""} placeholder={tr("e.g. Gothenburg")} onChange={(e) => setDraft({ ...draft, city: e.target.value })} style={inputStyle} />
              <button type="button" className="de-text-preset" disabled={saving} onClick={() => save({ city: draft.city })} style={{ width: "100%", marginTop: 6 }}>
                {saving ? tr("Saving…") : tr("Use this place")}
              </button>
            </div>
          )}

          {o.integrationId && o.dataType === "transit.departures" && (
            <div className="de-rs-field" style={{ marginBottom: 8 }}>
              <label>{tr("Stop")}</label>
              <StopPicker value={o.config?.stopName} onPick={(stop) => save({ ...stop, limit: 12 })} />
            </div>
          )}

          {error && <div style={{ color: "#b42318", fontSize: 12, marginBottom: 8 }}>{error}</div>}

          {o.dataType !== "weather.current" && (
            <div className="de-rs-field" style={{ marginBottom: 8 }}>
              <label>{tr("Rows")}</label>
              <select value={o.rows || 5} onChange={(e) => onPatch({ rows: parseInt(e.target.value, 10) })} style={{ width: "100%", height: 32, borderRadius: 6 }}>
                {[3, 4, 5, 6, 7, 8, 10].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          )}
          {color(tr("Background"), "bg", "#0f172a")}
          {color(tr("Text color"), "fg", "#ffffff")}
          <p style={{ fontSize: 11, color: "#667085", marginTop: 8 }}>
            {tr("Screens update every minute. If the source is down, the last fetched data stays on screen.")}
          </p>
        </>
      )}
    </div>
  );
}
